import { get, isArray, isEmpty, orderBy } from 'lodash';
import { toAllowedStringLength } from '../../domain/common/formattersToAllowedValueLength';
import { ICompany } from '../../domain/company/interface';
import { IEmailUtilityAutomation } from '../../domain/email-utility-automation/interface';

export const buildCompanyFilter = (companies: ICompany[]): { text: string; value: number }[] => {
    return orderBy(companies, 'company_name').map(c => {
        return { text: toAllowedStringLength(c.company_name), value: c.company_id };
    });
};

interface IControlProviderFilterOptions {
    includeWithoutControlProvider: boolean;
}

export const buildControlProviderFilter = (
    controlProviderCompanies: ICompany[],
    options?: IControlProviderFilterOptions
): { text: string; value: string }[] => {
    const filterOptions = controlProviderCompanies.map(c => {
        return { text: toAllowedStringLength(c.company_name, 30), value: c.company_id.toString() };
    });

    if (options?.includeWithoutControlProvider) {
        filterOptions.unshift({ text: 'No Control Provider', value: 'null' });
    }

    return filterOptions;
};

export const buildUtilityCustomerFilter = (utilityCustomerCompanies: ICompany[]): { text: string; value: string }[] => {
    const filterOptions = utilityCustomerCompanies.map(c => {
        return { text: toAllowedStringLength(c.company_name, 30), value: c.company_id.toString() };
    });

    filterOptions.unshift({ text: 'No Utility Customer', value: 'null' });

    return filterOptions;
};

export const buildEnrollmentIdFilter = (enrollmentIds: string[]): { text: string; value: string }[] => {
    return enrollmentIds.map(eId => {
        return { text: toAllowedStringLength(eId), value: eId };
    });
};

export const getColumnSortOrder = (
    field: string,
    sorter?: {
        field: string;
        order: 'descend' | 'ascend' | undefined;
    }
) => ({
    sortOrder: sorter && sorter.field === field ? sorter.order : undefined,
});

export const getColumnFilteredValue = (field: string, filter: any) => {
    const isFieldExist = filter && !isEmpty(filter) && get(filter, field) !== undefined;

    const getFieldValue = () => {
        const fieldValue = get(filter, field);
        return isArray(fieldValue) ? fieldValue : [fieldValue];
    };

    return {
        filteredValue: isFieldExist ? getFieldValue() : null,
    };
};

export function buildEmailUtilityAutomationsFilter(emailUtilityAutomations: IEmailUtilityAutomation[]) {
    return emailUtilityAutomations.map((automation: IEmailUtilityAutomation) => ({
        text: toAllowedStringLength(automation.title),
        value: automation.automation_key,
        key: automation.automation_key,
    }));
}

export function getEmailUtilityAutomationTitle(automationKey: string, emailUtilityAutomations: IEmailUtilityAutomation[]): string {
    return emailUtilityAutomations.find(emailUtilityAutomation => emailUtilityAutomation.automation_key === automationKey)?.title || '';
}

export type AgreementStatusFilterValue = 'active' | 'inactive' | 'expiring_soon' | 'expired_recently';

export type EventAnalyticsDataRangeFilterValues = 'all' | 'current_month' | 'current_quarter' | 'previous_quarter' | 'year_to_date' | 'prior_year';

export type EventAnalyticsDurationFilterValues = 'less_than_1' | '1_to_3' | 'more_than_3';

export type EventAnalyticNominationPresetFilterValues = 'all' | 'more_than_0';

export function sortByField<T extends object>(a: T, b: T, field: string): number {
    // Get values safely with fallbacks
    const valueA = get(a, field);
    const valueB = get(b, field);

    // Handle null/undefined values - put them at the end
    if (valueA == null && valueB == null) return 0;

    // Nulls/undefineds should be sorted to the end
    if (valueA == null) return -1;
    if (valueB == null) return 1;

    // Date detection and comparison
    if (valueA instanceof Date && valueB instanceof Date) {
        return valueA.getTime() - valueB.getTime();
    }

    // Try to parse strings that might be dates
    if (typeof valueA === 'string' && typeof valueB === 'string') {
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);

        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
            return dateA.getTime() - dateB.getTime();
        }
    }

    // Number comparison
    if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueA - valueB;
    }

    // String comparison (default)
    const stringA = String(valueA);
    const stringB = String(valueB);

    return stringA.localeCompare(stringB);
}
